'use client';

import { useState, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface ConditionHeroProps {
  category: string;
  title: string;
  description: string;
  imageUrl: string;
  imageAlt: string;
}

export default function ConditionHero({ category, title, description, imageUrl, imageAlt }: ConditionHeroProps) {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const scrollToProducts = () => {
    const productsSection = document.querySelector('section[data-section="products"]');
    if (productsSection) {
      productsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const scrollToEducation = () => {
    const educationSection = document.querySelector('section[data-section="education"]');
    if (educationSection) {
      educationSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  if (!isHydrated) {
    return (
      <section className="relative bg-gradient-to-br from-primary/5 to-secondary/5 py-20 lg:py-32">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-6">
                <span className="w-2 h-2 bg-primary rounded-full"></span>
                <span className="text-sm font-medium text-primary capitalize">{category}</span>
              </div>
              <h1 className="text-4xl lg:text-5xl font-heading font-semibold text-foreground mb-6">
                {title}
              </h1>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                {description}
              </p>
            </div>
            <div className="relative h-[400px] lg:h-[500px] rounded-2xl overflow-hidden shadow-elevation-xl">
              <AppImage
                src={imageUrl}
                alt={imageAlt}
                fill
                className="object-cover"
                priority
              />
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="relative bg-gradient-to-br from-primary/5 to-secondary/5 py-20 lg:py-32">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-6">
              <span className="w-2 h-2 bg-primary rounded-full animate-pulse"></span>
              <span className="text-sm font-medium text-primary capitalize">{category}</span>
            </div>
            <h1 className="text-4xl lg:text-5xl font-heading font-semibold text-foreground mb-6">
              {title}
            </h1>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              {description}
            </p>
            <div className="flex flex-wrap gap-4">
              <button 
                onClick={scrollToProducts}
                className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium transition-smooth hover:shadow-elevation-md hover:scale-[0.97]"
              >
                <Icon name="ShoppingCartIcon" size={20} />
                View Products
              </button>
              <button 
                onClick={scrollToEducation}
                className="flex items-center gap-2 px-6 py-3 border-2 border-primary text-primary rounded-lg font-medium transition-smooth hover:bg-primary hover:text-primary-foreground"
              >
                <Icon name="InformationCircleIcon" size={20} />
                Learn More
              </button>
            </div>
          </div>
          <div className="relative h-[400px] lg:h-[500px] rounded-2xl overflow-hidden shadow-elevation-xl">
            <AppImage
              src={imageUrl}
              alt={imageAlt}
              fill
              className="object-cover"
              priority
            />
          </div>
        </div>
      </div>
    </section>
  );
}